import { Visualizer, VisualizerControl, VisualizationUpdateLevel } from '../data-source/visualizer';
import { Datum } from '../data-source/types';
import { Map as TLMDMap } from '../map/map';
import { Timeline } from '../timeline/timeline';
import { Drawer } from '../panel/cabinets';
import { markup2element, replaceNodeContent, delegatedObserver } from '../dom/dom';
import { throttleTime } from 'rxjs/operators';
import { Method, ResponseType, xhrPromise, XHRPromise, XHRResult } from '../xhr/xhr';

import { jsonCopy, lookupProperty, hasProperty, defaultTo, merge } from '../util/util';
import { ThumbnailFactory } from '../util/thumbnail';

import { ObservationDataSource } from './observation';
import { ObservationContainer } from './container-observation';
import * as uuid from 'uuid-random';
import { RowComponent, TabulatorFull as Tabulator } from 'tabulator-tables';

export class ObservationDetailVisualizer implements Visualizer {

  private inspectionContainer: ObservationContainer;
  private source: ObservationDataSource;
  private refineSelectionTable: Tabulator;
  private refineSelectionTableReady: boolean

  private updatingSelection: boolean;

  private inEditMode: boolean;
  private submitHandler: () => void;

  private dataPromise: XHRPromise;
  private currentDatum: Datum;

  public constructor(private visualizerKey: string, private obsType: string, private drawer: Drawer) {
    this.inspectionContainer = <ObservationContainer>this.drawer.getContainer();
    this.source = this.inspectionContainer.getDataSource();

    let tabulatorElement = <HTMLElement>this.drawer.getContainerNode().querySelector('div.tlmd-content.details div.tlmd-data-none-mono-section div');
    let paginationElement = <HTMLElement>this.drawer.getContainerNode().querySelector('div.tlmd-data-none-mono-section .tlmd-list-pagination');


    this.updatingSelection = false;
    this.inEditMode = false;
    this.submitHandler = undefined;
    this.currentDatum = undefined;


	this.refineSelectionTableReady = false;
    this.refineSelectionTable = new Tabulator(tabulatorElement, {
      layout: 'fitColumns',
      layoutColumnsOnNewData: true,
      pagination: false,
      paginationMode: 'local',
      paginationElement: paginationElement,
      selectable: 1,
      columns: [
        this.inspectionContainer.getPropertyMap()['time'].tabulator,
        this.inspectionContainer.getPropertyMap()['geometry.label'].tabulator
      ],
      initialSort: [{ column: "time", dir: "desc" }]
    });
    this.refineSelectionTable.on("tableBuilt", () => { this.refineSelectionTableReady = true;});    
    this.refineSelectionTable.on("rowSelectionChanged", this.refineSelection.bind(this));    

    this.subscribeHandlers();
  }

  public release(): void {
    this.releaseAlpaca();
    this.releaseTabulator();
  }

  public key(): string {
    return this.visualizerKey + '_' + this.type();
  }

  public type(): string {
    return this.obsType;
  }

  public setControls(_controls: { [key: string]: VisualizerControl }): Visualizer {
    //no controls here
    return this;
  }

  public setTimeline(_timeline: Timeline): void {
    //we do not deal with the timeline here
  }

  public setMap(_map: TLMDMap): void {
    //we do not deal with the map here
  }


  /**
   * Only this visualiser will forward the update selection
   * to the observation container. The container will update
   * the inspector GUI by making sections hidden or visible.
   */
  public updateSelection(): void {
    if (!this.inEditMode) {
      if (!this.updatingSelection) {
        this.updatingSelection = true;
        let dataList = this.inspectionContainer.getDataList();

        this.inspectionContainer.setSelectedData(this.inspectionContainer.getDataList().getSelectionIds());

        let selectionSize = dataList.getSelectionSize();
        if (selectionSize > 0) {
          if (selectionSize === 1) {
            this.updateDetail(dataList.getSelection()[0]);
          } else {
            this.currentDatum = undefined;
            this.updateSubSelection(dataList.getSelection());
          }
        }
        this.updatingSelection = false;
      }
    }
  }

  public refresh(_level: VisualizationUpdateLevel): void {
    //no refreshing needed by this visualizer
  }
  private subscribeHandlers(): void {
    //container will unsubscribe in release cycle
    this.inspectionContainer.addSubscription(
      this.drawer.getSizeObservable().pipe(
        throttleTime(300, undefined, { leading: true, trailing: true })
      ).subscribe(_drawerSize => {
        //give tabulator a change to adapt to drawer size changes
        if(this.refineSelectionTableReady){
        	this.refineSelectionTable.redraw();
        }
      })
    )
    //make adding new entities work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-add`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (!this.inEditMode) {
          this.setEditMode(true, undefined);
        } else {
          //console.info('Already editing a record.');
        }
      })
    );

    //make modifying existing entities work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-modify`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (!this.inEditMode) {
          if (this.inspectionContainer.getDataList().getSelectionSize() === 1) {
            this.setEditMode(true, this.inspectionContainer.getDataList().getSelection()[0]);
          } else {
            //console.info('A single record needs to be selected.');
          }
        } else {
          //console.info('Already editing a record.');
        }
      })
    );

    //make canceling an edit work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-cancel`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (this.inEditMode) {
          if (this.inspectionContainer.getDataList().getSelectionSize() === 1) {
            this.setEditMode(false, this.inspectionContainer.getDataList().getSelection()[0]);
          } else {
            this.setEditMode(false, null);
          }
        } else {
          //console.info('Not in edit mode.');
        }
      })
    );

    //make saving an entity work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-save`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (this.inEditMode) {
          this.submitHandler();
        } else {
          //console.info('Not in edit mode.');
        }
      })
    );

    //make deleting existing entities work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-delete`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (!this.inEditMode) {
          if (this.inspectionContainer.getDataList().getSelectionSize() === 1) {
            let datum = this.inspectionContainer.getDataList().getSelection()[0];
            let promise = this.setTrashStatus(datum.id, true);
            if (promise) {
              promise.then((_response: XHRResult) => {
                datum.deleted = true;
                this.setTrashButtons(datum);
                this.inspectionContainer.getDataList().handleDatumDelete(datum, this.inspectionContainer.getAutoSelectOnDeletionId(datum.id));
                this.source.refresh(this.type());
              },
                () => {
                  console.error('Error while updating  entity thrash status');
                });
            } else {
              //console.warn('Already  storing entity');
            }
          } else {
            //console.info('A single record needs to be selected.');
          }
        } else {
          //console.info('Already editing a record.');
        }
      })
    );

    //make restoring existing entities work
    this.inspectionContainer.addSubscription(delegatedObserver(this.inspectionContainer.getContainerNode(), 'click', `a.tlmd-datum-restore`)
      .subscribe(evAndTrgt => {
        evAndTrgt.event.preventDefault();
        evAndTrgt.target.blur();
        if (!this.inEditMode) {
          if (this.inspectionContainer.getDataList().getSelectionSize() === 1) {
            let datum = this.inspectionContainer.getDataList().getSelection()[0];

            let promise = this.setTrashStatus(datum.id, false);
            if (promise) {
              promise.then((_response: XHRResult) => {
                datum.deleted = false;
                this.setTrashButtons(datum);
                this.source.refresh(this.type());
              },
                () => {
                  console.error('Error while updating  entity thrash status');
                });
            } else {
              //console.warn('Already  storing entity');
            }
          } else {
            //console.info('A single record needs to be selected.');
          }
        } else {
          //console.info('Already editing a record.');
        }
      })
    );

  }

  /**
   * Update the detail section
   */
  private updateDetail(datum: Datum): void {
    if (!this.inEditMode) {
      if (!this.isCurrentDatum(datum)) {

        let typeDefinition = this.inspectionContainer.getDefinition();
        this.resetAlpaca({
          data: datum,
          schema: typeDefinition.schema,
          options: Object.assign({}, (<{ options: any, view: any }>typeDefinition.views['record-display']).options, { hideInitValidationError: true }),
          view: (<{ options: any, view: any }>typeDefinition.views['record-display']).view
        });
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-cancel`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-save`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-add`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-modify`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));

        this.setTrashButtons(datum);
        this.currentDatum = datum;
      } else {
        //console.log('already displaying')
      }
    } else {
      //console.log('in edit mode');
    }
  }
  /**
   * create a list of data records where we can select one from
   */
  private updateSubSelection(data: Datum[]): void {
    if (this.refineSelectionTable != null && this.refineSelectionTableReady) {
      this.refineSelectionTable.setPageSize(data.length == 0 ? 1 : data.length);
      this.refineSelectionTable.setData(data);
    }
  }

  private refineSelection(data: any[], _rows: RowComponent[]): void {
    if (data.length > 0) {
      this.source.onSelection(this.type(), data[0].id);

    }
  }

  private isCurrentDatum(datum: Datum): boolean {
    if (!datum && !this.currentDatum) {
      return true;
    } else if (datum && this.currentDatum) {
      return datum.id == this.currentDatum.id &&
        this.source.getVersion(datum) == this.source.getVersion(this.currentDatum);
    }
    return false;
  }

  private releaseAlpaca(): void {
    let alpacaContainer = this.inspectionContainer.getContainerNode().querySelector('div.tlmd-content.details div.tlmd-datum-section div.tlmd-datum div.tlmd-alpaca');
    if (alpacaContainer) {
      //console.log('found alpaca container');
      if ((<any>$(alpacaContainer)).alpaca('exists')) {
        //console.log('alpaca exists in container');
      } else {
        //console.log('alpaca does not exists in container');
      }
      (<any>$(alpacaContainer)).alpaca('destroy');
    }
  }

  private resetAlpaca(alpacaDefinition?: any): HTMLDivElement {
    this.releaseAlpaca();
    let alpacaContainer = <HTMLDivElement>markup2element('<div class="tlmd-alpaca"></div>');
    replaceNodeContent(
      this.inspectionContainer.getContainerNode().querySelector('div.tlmd-content.details div.tlmd-datum-section div.tlmd-datum'),
      alpacaContainer);

    if (alpacaDefinition) {
      (<any>$(alpacaContainer)).alpaca(alpacaDefinition);
    }

    return alpacaContainer;
  }
  private getAlpaca(): any {
    let alpacaContainer = this.inspectionContainer.getContainerNode().querySelector('div.tlmd-content.details div.tlmd-datum-section div.tlmd-datum div.tlmd-alpaca');
    if (alpacaContainer) {
      return (<any>$(alpacaContainer));
    } else {
      return undefined;
    }
  }

  private releaseTabulator(): void {
    if (this.refineSelectionTable) {
      this.refineSelectionTable.destroy();
      this.refineSelectionTable = undefined;
    }
  }

  private setTrashButtons(datum: Datum): void {
    this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-delete`).forEach((anchor: HTMLAnchorElement) => anchor.classList.toggle('tlmd-hide', datum.deleted));
    this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-restore`).forEach((anchor: HTMLAnchorElement) => anchor.classList.toggle('tlmd-hide', !datum.deleted));
  }

  private setEditMode(edit: boolean, datum: Datum): void {
    if (!this.inEditMode && edit) {
      this.inEditMode = true;
      this.currentDatum = undefined;
      if (!datum) {
        //when adding a new observation, rest the search term
        this.inspectionContainer.setSearchTerm('');
        //when a new record gets added, we are only allowed to save after the wizard has gathered all data
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-save`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      } else {
        //only allow saving of records when modifying
        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-save`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
      }
      //deselect all data except the given datum.id
      this.source.onSelection(this.type(), datum ? [datum.id] : []);
      this.inspectionContainer.setSelectedData(datum ? [datum.id] : ['00000000-0000-0000-0000-000000000000']);

      //lock current selection
      this.inspectionContainer.getDataList().lockSelection(true);

      this.editDetail(datum);

      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-cancel`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-modify`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-add`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-delete`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-restore`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));

      this.inspectionContainer.switchToEditSection();

    } else if (this.inEditMode && !edit) {
      this.currentDatum = undefined;
      this.submitHandler = undefined;
      this.releaseAlpaca();

      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-cancel`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-save`).forEach((anchor: HTMLAnchorElement) => anchor.classList.add('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-add`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
      this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-modify`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));

      this.inEditMode = false;

      //unlock current selection
      this.inspectionContainer.getDataList().lockSelection(false);

      if (datum) {

        this.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-modify`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
        this.setTrashButtons(datum);

        // pan the map to include the new record
        let geometry = {
          north: this.source.getNorth(datum),
          east: this.source.getEast(datum),
          south: this.source.getSouth(datum),
          west: this.source.getWest(datum)
        }
        this.inspectionContainer.panMap(geometry);

        // select the new record after panning the timeline (actual selection takes place during call to inspectionContainer.panTimeline() )
        let dataList = this.source.getData()[this.type()];
        if (!dataList.getMeta().selected.includes(datum.id)) {
          dataList.getMeta().selected.push(datum.id);
        }

        // pan timeline to incude the new record
        let temporalRange = {
          start: this.source.getStart(datum),
          end: this.source.getEnd(datum)
        }
        this.inspectionContainer.panTimeline(temporalRange);
      } else {
        //let container decide what to show based on current selection
        this.inspectionContainer.setSelectedData(this.inspectionContainer.getDataList().getSelectionIds());
      }
    }
  }

  // TODO This method is a mess. It Uses alpaca, which depends on JQuery. Replace this one day
  private editDetail(datum: Datum): void {
    let formDefinition = this.inspectionContainer.getDefinition();
    let view = 'wizard';
    let capturers: any = undefined;//provided by (ionic)app but not used here
    let mapSettings: any = undefined;//provided by (ionic)app but not used here
    let attachmentsToken = uuid();//serves as a temporary document id used for document attachments

    let self = this;

    let alpacaContainer = this.resetAlpaca();

    //        $container.append("<div class='container-fluid'><div class='row'><div class='col-md-12'><div class='formAnchor'></div></div></div></div>");

    //obtain a deep copy of the schema and alpaca view since they will be modified
    let schema = jsonCopy(formDefinition.schema);
    let viewDefinition = jsonCopy(formDefinition.views[view]);

    // the viewDefinition gets fucked up while reusing it (after clicking back from the summary page, steps array grows)
    // so store the last page once
    let lastPage =
      hasProperty(viewDefinition, 'view.wizard.steps.length') ? viewDefinition.view.wizard.steps.length - 1 : undefined;

    //    let formAnchor = $('div.formAnchor', $container);
    let formAnchor = this.getAlpaca();

    let attachmentUrlPostfixAlternative = attachmentsToken + '?oa=true';

    // hide initial validation by default unless specified otherwise (same for each field below)
    viewDefinition.options.hideInitValidationError = defaultTo(viewDefinition.options.hideInitValidationError, true);

    Object.values(viewDefinition.options.fields).forEach((fieldOptions: any) => {
      fieldOptions.hideInitValidationError = defaultTo(fieldOptions.hideInitValidationError, true);

      if (fieldOptions.type === "datetimeCustom") {
        let now = Date.now();
        //let now = moment();
        if (fieldOptions.offsetMin != null) {
          fieldOptions.picker.minDate = now + fieldOptions.offsetMin;
          //fieldOptions.picker.minDate = now.clone().add(fieldOptions.offsetMin, "ms");
        }
        if (fieldOptions.offsetMax != null) {
          fieldOptions.picker.maxDate = now + fieldOptions.offsetMax;
          //fieldOptions.picker.maxDate = now.clone().add(fieldOptions.offsetMax, "ms");
        }
      } else if (fieldOptions.type === "location") {
        fieldOptions.locationCapturer = capturers ? capturers.location : undefined;
        if (this.source.getMap() != null) {
          fieldOptions.mapSettings = this.source.getMap().getMapSettings();
        } else if (mapSettings != null) {
          fieldOptions.mapSettings = jsonCopy(mapSettings);
        }
      } else if (fieldOptions.type === 'attachment' || fieldOptions.type === 'attachments') {
        fieldOptions.urlPostfixAlternative = attachmentUrlPostfixAlternative;

        var maxWidth = fieldOptions.thumbnail && fieldOptions.thumbnail.maxWidth ? fieldOptions.thumbnail.maxWidth : undefined;
        var maxHeight = fieldOptions.thumbnail && fieldOptions.thumbnail.maxHeight ? fieldOptions.thumbnail.maxHeight : 56;

        if (fieldOptions.upload == null) fieldOptions.upload = {};
        fieldOptions.upload.autoUpload = true;
        fieldOptions.mediaCapturers = capturers;
        fieldOptions.createThumbnail = (data: any) => {
          return ThumbnailFactory.create(data.files[0], maxWidth, maxHeight).then((canvas: HTMLCanvasElement) => {
            var thumbnailDataURL = canvas.toDataURL();
            data.files[0].thumbnailDataUrl = thumbnailDataURL;
            data.files[0].hasThumbnail = true;
            if (data.formData == null) {
              data.formData = {};
            }
            data.formData['thumbnail_' + data.files[0].name] = thumbnailDataURL;
            console.log("Thumbnail created!");
          },
            (): void => {
              data.files[0].hasThumbnail = false;
            });
        };
      }
    });

    this.submitHandler = function() {
      console.log('submitting: alpaca.alpaca()=%o', self.getAlpaca().alpaca());
      console.log('submitting : alpaca.alpaca().getValue()=%o', self.getAlpaca().alpaca().getValue());
      console.log('submitting : alpaca.alpaca().isValid()=%o', self.getAlpaca().alpaca().isValid());
      let datum = self.getAlpaca().alpaca().getValue();
      console.log('submitting entity %o', datum);
      if (datum) {
        // note: this already does the actual POSTing of data
        // on desktop that would close the wizard window, on mobile app this would navigate back to the home-page/screen
        let promise = self.storeEntity(datum, attachmentsToken);
        if (promise) {
          promise.then((response: XHRResult) => {
            let id: string = undefined;
            if (response.method === Method.post) {
              //show thank you  when 
              //let msg = viewDefinition.options.thankYouPage.markup
              if (response.data && response.data.length === 1) {
                id = response.data[0].id;
              } else {
                console.warn(`Expected one datum id back. Response = %o`, response.data);
              }
            } else {
              id = datum.id;
            }
            if (id) {
              self.getEntity(id)
                .then((response) => {
                  if (response.data && response.data.length === 1) {
                    let datum = <Datum>response.data[0];
                    console.info(`New/modified  datum ${id} = %o`, datum);
                    self.setEditMode(false, datum);
                  } else {
                    console.warn(`Expected one datum  back. Response = %o`, response.data);
                    self.setEditMode(false, undefined);
                  }
                })
                .catch((error) => {
                  console.error(`Unable to retrieve new/modified  datum ${id}. Error = %o`, error);
                  self.setEditMode(false, undefined);
                });
            } else {
              self.setEditMode(false, undefined);

            }
          },
            () => {
              console.error('Error while storing entity');
            });
        } else {
          console.warn('Already  storing entity');
        }
      } else {
        console.warn('No entity to store');
      }
    }

    if (view === "wizard") {
      // every type should at least (in addition to the wizard) define this (record-display) view
      // this ensures that the server side obs js sets the correct display only settings for
      // custom controls
      let summaryViewDefinition = {
        schema: schema,
        view: 'tlmd-display',
        options: Object.assign({}, formDefinition.views['record-display'].options,
          {
            form: {
              buttons: {
                previous: {
                  title: viewDefinition.view.wizard.buttons.previous.title,
                  click: function() {
                    viewDefinition.data = this.getValue();
                    // we can't immediately trigger the moveToStep event since the form
                    // isn't ready/around at all, leave a flag behind, this gets used inside
                    // postRender handler
                    viewDefinition._jumpToPage = true;
                    self.resetAlpaca(viewDefinition);
                  }
                },
                submit: {
                  value: viewDefinition.view.wizard.buttons.submit.title,
                  click: this.submitHandler
                }
              }
            }
          }),
        data: <any>undefined
      };
      //the summary view needs to know where to get thumbnails from
      Object.values(summaryViewDefinition.options.fields).forEach((fieldOptions: any) => {
        if (fieldOptions.type === 'attachment' || fieldOptions.type === 'attachments') {
          fieldOptions.urlPostfixAlternative = attachmentUrlPostfixAlternative;
        }
      });

      viewDefinition.view.wizard.buttons.submit.title =
        viewDefinition.view.wizard.buttons.next.title;

      /*
          variants for long form handling
          - add class long-wizard to take css into effect which downscales everything a bit
          and hides the description of each step. Leaves the ability to jump between steps intact
          - hide step bar and progress bar and listen to the custom index-changed event and adjust
          step description manually
       */

      formAnchor.on('index-changed', function(_evt: Event, currentIndex: number) {

        // inside the default wizard markup, there is exactly one legend element
        // which holds the schema title
        // optional: add description as well

        var label = viewDefinition.view.wizard.steps[currentIndex].title;
        $('legend', formAnchor).text(viewDefinition.schema.title + " / " + label);
        if (viewDefinition.view.wizard.steps[currentIndex].description)
          $('div.tlmd-container-description', formAnchor).text(viewDefinition.view.wizard.steps[currentIndex].description);
        else // steps with empty description (resolving to undefined in configuration) would not clear the description element
          $('div.tlmd-container-description', formAnchor).text("");
      });

      merge(viewDefinition, {
        view: {
          wizard: {
            showSteps: false,
            showProgressBar: false
          }
        }
      });


      viewDefinition.view.wizard.buttons.submit.click = function() {
        // re-render the same thing as read-only
        summaryViewDefinition.data = this.getValue();
        self.resetAlpaca(summaryViewDefinition);
        //enable the save button. In case of new observations this button was hidden
        self.inspectionContainer.getContainerNode().querySelectorAll(`a.tlmd-datum-save`).forEach((anchor: HTMLAnchorElement) => anchor.classList.remove('tlmd-hide'));
      };

      viewDefinition["postRender"] = (alpacaForm: any) => {
        // console.log("START POST RENDERING %o",alpacaForm);

        if (viewDefinition._jumpToPage && lastPage) {
          // skipping validation should be fine, otherwise we would not have come to the summary page at all
          self.getAlpaca().alpaca("get").trigger("moveToStep",
            { index: lastPage, skipValidation: true });
        }

        //let  postRenderValue = alpacaForm.getContainerValue();
        //console.log ('Intial value: %o',data);       
        //console.log ('post render value: %o',postRenderValue);


        //some dateTime fields need to update their time stamp if the page they have been bound to receives focus for the first time
        var handleFocus = function _handleFocus(_event: Event, field: any, timestamp: any) {
          if (!field.hasBeenFocussed) {
            //do we have a parent (are we a sub field)
            if (field.parent) {
              if (!field.parent.hasBeenFocussed) {//if the parent has not received focus before do:

                (<any[]>field.parent.children).forEach(function(child) {//iterate over all the children of the parent 
                  //if the child is a hidden timestamp control that uses current time and had no start value, update its value ?
                  if (child.type == 'datetimeCustom' && child.options.hidden && child.options.picker && child.options.picker.useCurrent && self.jsonPointer(child.path, datum) == null) {
                    //console.log("AUTO UPDATE TIMESTAMP FOR %o: %o event=%o time=%o curValue=%o firstValue=%o",child.name,child,event,timestamp,child.getValue(),com.tlmapp.jsonPointer(child.path, data));
                    child.setValue(timestamp);
                  }
                  field.hasBeenvisited = alpacaForm.wizardConfigs.bindings[field.name] == 1;
                });
                field.parent.hasBeenFocussed = true;
                //it seems we never come here. log to be sure
                console.log('CALLED _recordFocusTime(event, field.parent, timestamp); BUT NEVER FOUND THE IMPLEMENTATION');
              }
            }
            field.hasBeenFocussed = true;
          }
        };

        //define this recursive function that applies onFocus handlers to all fields in the form and sets inital visit/focus flags
        var flagField = function _flagField(field: any) {
          if (field.parent)
            field.hasBeenFocussed = false;
          field.on('focus', function(event: Event) {
            var now = Date.now();
            //                console.log('FOCUS \nevent=%o\nfield=%o',event,this,now);
            handleFocus(event, this, now);
          });
          if (field.children) {
            (<any[]>field.children).forEach(_flagField);
          }
        };

        //run the previous defined function
        (<any[]>alpacaForm.children).forEach(function(field) {
          flagField(field);
          field.hasBeenvisited = alpacaForm.wizardConfigs.bindings[field.name] == 1;
        });

        $(alpacaForm.domEl).on("index-changed", function(_event, stepIndex) {
          var now = Date.now();
          //var elem = this;
          var binding = stepIndex + 1;
          var fieldNamesInStep: string[] = Object.keys(alpacaForm.wizardConfigs.bindings).filter(fieldName => alpacaForm.wizardConfigs.bindings[fieldName] == binding);

          // console.log("THE WIZARDS SPEAKS AGAIN!\nthis=%o\nevent=%o\nstepIndex=%o\nfields=%o", this,event,stepIndex,fieldNamesInStep);

          fieldNamesInStep.forEach(function(fieldName) {
            var fieldControl = alpacaForm.childrenByPropertyId[fieldName];

            if (!fieldControl.hasBeenvisited) {
              fieldControl.hasBeenvisited = true;
              //console.log('%s HAS NOT BEEN VISITED BEFORE %o',fieldName,fieldControl);
              if (fieldControl.type == 'datetimeCustom' && fieldControl.options.picker && fieldControl.options.picker.useCurrent && self.jsonPointer(fieldControl.path, datum) == null) {
                //console.log("AUTO UPDATE TIMESTAMP FOR %o: %o event=%o time=%o curValue=%o firstValue=%o",fieldControl.name,fieldControl,event,now,fieldControl.getValue(), com.tlmapp.jsonPointer(fieldControl.path, data));
                fieldControl.setValue(now);
              }
            } else {
              //console.log('%s HAS BEEN VISITED BEFORE',fieldName);
            }

          });


        });
        //console.log("END POST RENDERING");

      };
    } else {
      if (lookupProperty(viewDefinition, 'form.buttons.submit') != null) {
        viewDefinition.form.buttons.submit.click = this.submitHandler;
      }
    }

    viewDefinition.data = datum;
    viewDefinition.schema = schema;

    (<any>$(alpacaContainer)).alpaca(viewDefinition);
  }


  private getEntity(entityId: string): Promise<XHRResult> {
    let url = `/${this.inspectionContainer.getApplication().getDomain()}/observation/datum/${this.source.getName()}/${this.type()}/${entityId}`;
    return this.dataPromise = xhrPromise({
      url: url,
      method: Method.get,
      digester: this.inspectionContainer.getApplication().getDigester(),
      responseType: ResponseType.json
    });
  }

  private storeEntity(entity: any, attachmentsToken: string): Promise<XHRResult> {
    //abort running data request when the options has been configured that way and a request is running
    if (this.dataPromise && this.dataPromise.canAbort()) {
      this.dataPromise.abort();
      this.dataPromise = null;
    }

    //when there is no active data request running
    if (!this.dataPromise) {

      let method = entity.id != null && entity.id !== 0 && entity.id !== '0' && entity.id !== '00000000-0000-0000-0000-000000000000' && entity.id !== '' ? Method.put : Method.post;
      let url = `/${this.inspectionContainer.getApplication().getDomain()}/observation/datum/${this.source.getName()}/${this.type()}?at=${attachmentsToken}`

      if (method === Method.put) {
        entity = merge(this.inspectionContainer.getDataList().get(entity.id), entity);
      }

      this.dataPromise = xhrPromise({
        url: url,
        method: method,
        digester: this.inspectionContainer.getApplication().getDigester(),
        responseType: ResponseType.json,
        data: { entity: JSON.stringify(entity, null, 2), mimeType: 'application/json' },
        noAbort: true
      });

      return this.dataPromise.then(response => {
        this.dataPromise = null;
        return response;
      }).catch((error) => {
        this.dataPromise = null;
        console.log('Error while storing entity. ', error);
        return error;
      });
    }

    return undefined;
  }

  private setTrashStatus(datumId: string, trash: boolean): Promise<XHRResult> {

    //abort running data request when the options has been configured that way and a request is running
    if (this.dataPromise && this.dataPromise.canAbort()) {
      this.dataPromise.abort();
      this.dataPromise = null;
    }

    //when there is no active data request running
    if (!this.dataPromise) {

      let method = Method.delete;
      let url = `/${this.inspectionContainer.getApplication().getDomain()}/observation/datum/${this.source.getName()}/${this.type()}/${datumId}?_trash=${trash}`;

      this.dataPromise = xhrPromise({
        url: url,
        method: method,
        digester: this.inspectionContainer.getApplication().getDigester(),
        responseType: ResponseType.json,
        noAbort: true
      });

      return this.dataPromise.then(response => {
        this.dataPromise = null;
        return response;
      }).catch((error) => {
        this.dataPromise = null;
        console.log('Error while updating datum\'s trash status.', error);
        return error;
      });
    }

    return undefined;
  }

  private jsonPointer(jsonPointer: string, obj: any) {
    var result = null;

    if (obj && jsonPointer) {
      // strip off the leading "/" if it is there
      jsonPointer = jsonPointer.indexOf("/") === 0 ? jsonPointer.substring(1) : jsonPointer;
      // strip off the trailing "/" if it is there
      jsonPointer = jsonPointer.endsWith("/") ? jsonPointer.substring(0, jsonPointer.length - 1) : jsonPointer;

      var current = obj;

      var pathArray = jsonPointer.split('/');
      for (var i = 0; i < pathArray.length; i++) {
        var pathElement = pathArray[i];

        var _name = pathElement;
        var _index = -1;

        var z1 = pathElement.indexOf("[");
        if (z1 >= 0) {
          var z2 = pathElement.indexOf("]", z1 + 1);
          if (z2 >= 0) {
            _index = parseInt(pathElement.substring(z1 + 1, z2));
            _name = pathElement.substring(0, z1);
          }
        }

        if (_name) {
          current = current[_name];

          if (_index > -1) {
            current = current[_index];
          }
        }
      }

      result = current;
    }

    return result;
  }

}